import { useMemo } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { useTheme } from '@/libraries/design/utils/dark-theme-provider';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { LogoV3 } from '@/libraries/wpl/foundations/src/logo-v3';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';
import { MotionContainer } from '@/patterns/global-nav/src/components/motion-container';
import {
	DivOnHoverExit,
	type GlobalNavLinkContentProps,
} from '@/patterns/global-nav/src/nav-links-container';
import { ProductPageNav } from '@/patterns/global-nav/src/product-page-nav';

import { LoomFeaturesDropdown } from './dropdowns/loom-features-dropdown';
import { LoomSolutionsDropdown } from './dropdowns/loom-solutions-dropdown';

const wrapSubnav = (subnav: JSX.Element) => {
	return (
		<DivOnHoverExit css={css({ position: 'relative' })}>
			<TypographyStyles>
				<MotionContainer direction="none">
					<div
						css={css({
							paddingBlock: token('space.500'),
							paddingInline: token('space.500'),
						})}
					>
						{subnav}
					</div>
				</MotionContainer>
			</TypographyStyles>
		</DivOnHoverExit>
	);
};

const LoomLogoComponent = () => {
	const { isDarkTheme } = useTheme();
	return <LogoV3 brand="loomcircular" type="logo" appearance="brand" lightMode={isDarkTheme} />;
};

export const LoomNav = () => {
	const intl = useIntl();

	const dropdownStyles = useMemo(() => {
		return {
			width: 'fit-content !important',
			margin: 0,
			insetBlockStart: '4.25rem',
		};
	}, []);

	const signupLink = useLocalizedHref('/try/cloud/signup?bundle=loom&edition=premium');
	const resourcesHref = useLocalizedHref('/software/loom/resources');
	const guidesHref = useLocalizedHref('/software/loom/resources/guides/getting-started');
	const pricingHref = useLocalizedHref('/software/loom/pricing');

	const links: GlobalNavLinkContentProps[] = [
		{
			globalLinksKey: 'features',
			productKey: 'loom',
			linkText: intl.formatMessage({
				id: 'LoomPageTemplate.navbar.features',
				defaultMessage: 'Features',
			}),
			hasDropdown: true,
			content: wrapSubnav(<LoomFeaturesDropdown />),
			containerPosition: 'relative',
		},
		{
			globalLinksKey: 'solutions',
			productKey: 'loom',
			linkText: intl.formatMessage({
				id: 'LoomPageTemplate.navbar.resources',
				defaultMessage: 'Solutions',
			}),
			hasDropdown: true,
			containerStyles: dropdownStyles,
			content: wrapSubnav(<LoomSolutionsDropdown />),
			containerPosition: 'relative',
		},
		{
			globalLinksKey: 'resources',
			productKey: 'loom',
			linkText: intl.formatMessage({
				id: 'LoomPageTemplate.navbar.useCases',
				defaultMessage: 'Resources',
			}),
			hasDropdown: false,
			href: resourcesHref,
		},
		{
			globalLinksKey: 'guides',
			productKey: 'loom',
			linkText: intl.formatMessage({
				id: 'LoomPageTemplate.navbar.guides',
				defaultMessage: 'Guides',
			}),
			hasDropdown: false,
			href: guidesHref,
		},
		{
			globalLinksKey: 'pricing',
			productKey: 'loom',
			linkText: intl.formatMessage({
				id: 'LoomPageTemplate.navbar.pricing',
				defaultMessage: 'Pricing',
			}),
			hasDropdown: false,
			href: pricingHref,
		},
	];

	return (
		<ProductPageNav
			productKey={'loom'}
			productLogoUrl="/software/loom"
			productLogo={<LoomLogoComponent />}
			productLogoAriaLabel={intl.formatMessage({
				id: 'LoomPageTemplate.navbar.loom-logo',
				defaultMessage: 'Loom logo',
			})}
			mobileCtaText={intl.formatMessage({
				id: 'LoomPageTemplate.navbar.getitfree',
				defaultMessage: 'Get it Free',
			})}
			navigationTrynowCtaText={intl.formatMessage({
				id: 'LoomPageTemplate.navbar.getitfree',
				defaultMessage: 'Get it Free',
			})}
			mobileCtaUrl={signupLink}
			mobileLogo={<LoomLogoComponent />}
			productPageNavLinks={links}
			navigationTrynowCtaUrl={signupLink}
			mobileLinksSections={[
				{
					mobileLinkKey: 'mobile-loom',
					mobileLinksProps: links,
				},
			]}
			additionalMenuPageObjectsArray={[
				{
					menuPageKey: 'features',
					menuPageObjects: <LoomFeaturesDropdown />,
				},
				{
					menuPageKey: 'solutions',
					menuPageObjects: <LoomSolutionsDropdown />,
				},
			]}
		/>
	);
};

export default LoomNav;
